import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Chip,
    CircularProgress,
    Fab,
    Switch,
    FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getFlashBetQuestionTemplates, toggleFlashBetQuestionTemplateEnabled } from '../Api';
import FlashBetQuestionTemplateDto from '../models/dtos/FlashBetQuestionTemplateDto';
import QuestionTemplateModal from '../components/QuestionTemplateModal';

const QuestionTemplatesPage: React.FC = () => {
    const [questionTemplates, setQuestionTemplates] = useState<FlashBetQuestionTemplateDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedTemplate, setSelectedTemplate] = useState<FlashBetQuestionTemplateDto | null>(null);

    useEffect(() => {
        loadQuestionTemplates();
    }, []);

    const loadQuestionTemplates = async () => {
        setLoading(true);
        try {
            const result = await getFlashBetQuestionTemplates();
            setQuestionTemplates(result);
        } catch (error) {
            console.error('Error loading question templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTemplate = () => {
        setSelectedTemplate(null);
        setModalOpen(true);
    };

    const handleEditTemplate = (template: FlashBetQuestionTemplateDto) => {
        setSelectedTemplate(template);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedTemplate(null);
    };

    const handleSaveSuccess = () => {
        loadQuestionTemplates();
        handleCloseModal();
    };

    const handleToggleEnabled = async (template: FlashBetQuestionTemplateDto) => {
        try {
            const result = await toggleFlashBetQuestionTemplateEnabled(template.id);
            if (result) {
                // Update the specific template in the list
                setQuestionTemplates(prev => 
                    prev.map(t => t.id === template.id ? result : t)
                );
            }
        } catch (error) {
            console.error('Error toggling template status:', error);
        }
    };

    const formatLogic = (logic: string): string => {
        return logic.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                FlashBet Question Templates
            </Typography>
            
            <Grid container spacing={3}>
                {questionTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <Card 
                            sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                opacity: template.enabled ? 1 : 0.7,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Typography variant="h6" component="h2" sx={{ flex: 1, mr: 1 }}>
                                        {template.title}
                                    </Typography>
                                    <Chip 
                                        label={template.enabled ? 'Enabled' : 'Disabled'} 
                                        color={template.enabled ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>

                                <Typography variant="body2" color="text.secondary" mb={1}>
                                    <strong>Logic:</strong> {formatLogic(template.logic)}
                                </Typography>

                                {template.allowedLocations && template.allowedLocations.length > 0 && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Locations:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {template.allowedLocations.map((location) => (
                                                <Chip 
                                                    key={location}
                                                    label={location.replace(/_/g, ' ')}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {template.answers && template.answers.length > 0 && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Answers ({template.answers.length}):
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {template.answers.slice(0, 3).map((answer, index) => (
                                                <Chip 
                                                    key={answer.id}
                                                    label={answer.name}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                            {template.answers.length > 3 && (
                                                <Chip 
                                                    label={`+${template.answers.length - 3} more`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {template.parameters && Object.keys(template.parameters).length > 0 && (
                                    <Box mb={1}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Parameters:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {Object.entries(template.parameters).slice(0, 2).map(([key, value]) => (
                                                <Chip 
                                                    key={key}
                                                    label={`${key}: ${value}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            ))}
                                            {Object.keys(template.parameters).length > 2 && (
                                                <Chip 
                                                    label={`+${Object.keys(template.parameters).length - 2} more`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                            
                            <CardActions>
                                <Button 
                                    size="small" 
                                    onClick={() => handleEditTemplate(template)}
                                >
                                    Edit
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={template.enabled}
                                            onChange={() => handleToggleEnabled(template)}
                                            size="small"
                                        />
                                    }
                                    label={template.enabled ? 'On' : 'Off'}
                                    sx={{ ml: 'auto', mr: 0 }}
                                />
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Fab
                color="primary"
                aria-label="add"
                onClick={handleAddTemplate}
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
            >
                <AddIcon />
            </Fab>

            <QuestionTemplateModal
                open={modalOpen}
                template={selectedTemplate}
                onSave={handleSaveSuccess}
                onClose={handleCloseModal}
            />
        </Container>
    );
};

export default QuestionTemplatesPage;